import React, { memo, useMemo } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Folder, Users, Calendar, TrendingUp, Edit2 } from "lucide-react";
import { cn } from "../../utils/cn";
import { projectStatusOptions, projectPriorityOptions } from "../../utils/constants";
import { formatDate } from "../../utils/scoring";
import { Project } from "../../types";

interface ProjectDetailViewProps {
    project: Project | null;
    onBack: () => void;
    onEdit: () => void;
    onDelete?: () => void;
}

/**
 * ProjectDetailView - Header section for a selected project
 * Shows project info and allows navigation back to project list
 */
export const ProjectDetailView = memo(function ProjectDetailView({
    project,
    onBack,
    onEdit,
    onDelete,
}: ProjectDetailViewProps) {
    if (!project) return null;

    // Get status config
    const statusConfig = useMemo(() =>
        projectStatusOptions.find(s => s.value === project.status) || projectStatusOptions[0],
        [project.status]
    );

    // Get priority config  
    const priorityConfig = useMemo(() =>
        projectPriorityOptions.find(p => p.value === project.priority) || projectPriorityOptions[1],
        [project.priority]
    );

    // Calculate lane percentages
    const lanePercentages = useMemo(() => {
        const total = project.epicCount || 1;
        return {
            now: Math.round(((project.laneCounts?.Now || 0) / total) * 100),
            next: Math.round(((project.laneCounts?.Next || 0) / total) * 100),
            later: Math.round(((project.laneCounts?.Later || 0) / total) * 100),
            unassigned: Math.round(((project.laneCounts?.Unassigned || 0) / total) * 100),
        };
    }, [project.epicCount, project.laneCounts]);

    return (
        <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="px-6 py-4 border-b border-siemens-deep-blue/5 dark:border-siemens-divider bg-gradient-to-r from-siemens-petrol/5 to-transparent dark:from-siemens-petrol/10"
        >
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 mb-3">
                <button
                    onClick={onBack}
                    className="flex items-center gap-1.5 text-sm font-medium text-siemens-petrol hover:text-siemens-petrol-dark transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    All Projects
                </button>
                <span className="text-siemens-text-soft">/</span>
                <span className="text-sm font-bold text-siemens-deep-blue dark:text-white">
                    {project.name}
                </span>
            </div>

            {/* Project Header */}
            <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={cn(
                        "w-12 h-12 rounded-xl flex items-center justify-center",
                        statusConfig.color + "/20"
                    )}>
                        <Folder className={cn("w-6 h-6", statusConfig.color.replace("bg-", "text-"))} />
                    </div>

                    {/* Info */}
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <h1 className="text-lg font-black text-siemens-deep-blue dark:text-white">
                                {project.name}
                            </h1>
                            <span className={cn(
                                "text-[10px] font-bold uppercase px-2 py-0.5 rounded",
                                statusConfig.color + "/20",
                                statusConfig.color.replace("bg-", "text-")
                            )}>
                                {statusConfig.label}
                            </span>
                            <span className={cn("text-[10px] font-bold uppercase", priorityConfig.color)}>
                                {priorityConfig.label} priority
                            </span>
                        </div>

                        {project.description && (
                            <p className="text-sm text-siemens-text-soft max-w-xl mb-3">
                                {project.description}
                            </p>
                        )}

                        {/* Meta row */}
                        <div className="flex items-center gap-4 text-xs text-siemens-text-soft">
                            {project.owner && (
                                <div className="flex items-center gap-1.5">
                                    <Users className="w-3.5 h-3.5" />
                                    <span>{project.owner}</span>
                                </div>
                            )}
                            {project.createdAt && (
                                <div className="flex items-center gap-1.5">
                                    <Calendar className="w-3.5 h-3.5" />
                                    <span>Created {formatDate(project.createdAt)}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                    <button
                        onClick={onEdit}
                        className="h-9 px-3 border border-siemens-deep-blue/10 dark:border-siemens-divider rounded-lg flex items-center gap-2 hover:bg-slate-50 dark:hover:bg-siemens-elevation-selected transition-colors"
                    >
                        <Edit2 className="w-4 h-4 text-siemens-text-soft" />
                        <span className="text-sm font-medium text-siemens-deep-blue dark:text-white">Edit</span>
                    </button>
                </div>
            </div>

            {/* Stats Bar */}
            <div className="flex items-center gap-6 mt-4 pt-4 border-t border-siemens-deep-blue/5 dark:border-siemens-divider">
                <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-siemens-petrol" />
                    <span className="text-lg font-black text-siemens-deep-blue dark:text-white">
                        {project.epicCount || 0}
                    </span>
                    <span className="text-sm text-siemens-text-soft">epics</span>
                </div>

                <div className="flex items-center gap-2">
                    <span className="text-sm text-siemens-text-soft">Avg WSJF:</span>
                    <span className="text-sm font-bold text-siemens-petrol">
                        {(project.avgWsjf || 0).toFixed(1)}
                    </span>
                </div>

                {/* Lane distribution */}
                <div className="flex items-center gap-3 ml-auto">
                    <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-siemens-feedback-green" />
                        <span className="text-xs font-medium text-siemens-deep-blue dark:text-white">
                            {project.laneCounts?.Now || 0} Now ({lanePercentages.now}%)
                        </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-siemens-feedback-orange" />
                        <span className="text-xs font-medium text-siemens-deep-blue dark:text-white">
                            {project.laneCounts?.Next || 0} Next ({lanePercentages.next}%)
                        </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-siemens-feedback-blue" />
                        <span className="text-xs font-medium text-siemens-deep-blue dark:text-white">
                            {project.laneCounts?.Later || 0} Later ({lanePercentages.later}%)
                        </span>
                    </div>
                </div>
            </div>
        </motion.div>
    );
});

export default ProjectDetailView;
